import {
    WorkflowNode,
    WorkflowEdge,
    TriggerData,
    TransformData,
} from './types';

export interface VariableSuggestion {
    /** The JSONata path to insert, e.g. "lookup.body.email". */
    path: string;
    /** Where it came from, for the dropdown label. */
    source: string;
}

export function nodeKeyOf(node: WorkflowNode): string {
    return node.key && node.key.length > 0 ? node.key : node.id;
}

/** Output field names a node exposes to downstream nodes. */
export function outputFieldsForNode(node: WorkflowNode): string[] {
    switch (node.type) {
        case 'trigger': {
            const data = node.data as TriggerData;
            switch (data.triggerType) {
                case 'event':
                    return ['name', 'payload', 'occurredAt', 'eventId', 'raw'];
                case 'share':
                    return [
                        'shareIdentifiers',
                        'shareIdentifier',
                        'destination',
                        'method',
                        'shareType',
                        'galleryId',
                        'activationId',
                        'occurredAt',
                        'raw',
                    ];
                case 'mediaUploaded':
                    return [
                        'mediaId',
                        'shareIdentifier',
                        'url',
                        'thumbnailUrl',
                        'mimeType',
                        'type',
                        'fileSize',
                        'width',
                        'height',
                        'aspectRatio',
                        'smileportType',
                        'galleryId',
                        'activationId',
                        'capturedAt',
                        'occurredAt',
                        'raw',
                    ];
                default: {
                    const inputName = data.inputName || 'raw';
                    return [inputName, 'data'];
                }
            }
        }
        case 'transform': {
            const data = node.data as TransformData;
            return (data.assignments ?? [])
                .map((a) => a.key)
                .filter((k) => k && k.length > 0);
        }
        case 'httpRequest':
            return ['status', 'headers', 'body'];
        case 'lookupMedia':
            return ['media', 'count', 'shareIdentifiers'];
        default:
            // branch and emitCodes produce no referenceable output.
            return [];
    }
}

/** Ids of all nodes that can reach `targetId` by following edges backward. */
export function ancestorIds(
    targetId: string,
    edges: WorkflowEdge[],
): Set<string> {
    const ancestors = new Set<string>();
    const queue = [targetId];
    while (queue.length > 0) {
        const current = queue.shift() as string;
        edges
            .filter((e) => e.target === current)
            .forEach((e) => {
                if (!ancestors.has(e.source)) {
                    ancestors.add(e.source);
                    queue.push(e.source);
                }
            });
    }
    return ancestors;
}

/**
 * Build the list of JSONata variable suggestions available to a node, based on
 * the outputs of every upstream (ancestor) node.
 */
export function suggestionsForNode(
    nodeId: string,
    nodes: WorkflowNode[],
    edges: WorkflowEdge[],
): VariableSuggestion[] {
    const ancestors = ancestorIds(nodeId, edges);
    const suggestions: VariableSuggestion[] = [];

    nodes
        .filter((n) => ancestors.has(n.id) && n.id !== nodeId)
        .forEach((node) => {
            const key = nodeKeyOf(node);
            const label = node.label || key;
            const fields = outputFieldsForNode(node);
            if (fields.length === 0) {
                return;
            }
            fields.forEach((field) => {
                suggestions.push({
                    path: `${key}.${field}`,
                    source: `${label} (${node.type})`,
                });
            });
        });

    return suggestions;
}
